import React, { useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import AppShell from "../components/AppShell";
import ProjectLimitNotice from "../components/ProjectLimitNotice";
import { api } from "../lib/api";
import { CaretLeft, CaretRight, Check } from "@phosphor-icons/react";
import { useT } from "../lib/i18n";
import { useAuth } from "../lib/auth";
import WorkspaceLoading from "../components/WorkspaceLoading";

function stepClass(i, step) {
  if (i === step) return "bg-cyan-500/15 text-cyan-300 border border-cyan-500/30";
  if (i < step) return "text-cyan-400";
  return "text-slate-500";
}

export default function NewProject() {
  const nav = useNavigate();
  const [searchParams] = useSearchParams();
  const existingProjectId = searchParams.get("project_id") || "";
  const existingDeviceId = searchParams.get("device_id") || "";
  const t = useT();
  const { user } = useAuth();
  const [step, setStep] = useState(0);
  const [submitting, setSubmitting] = useState(false);
  const [err, setErr] = useState("");
  const [checkingProjectLimit, setCheckingProjectLimit] = useState(true);
  const [projectLimitNotice, setProjectLimitNotice] = useState(null);
  const [existingAssessmentLoadError, setExistingAssessmentLoadError] = useState(false);
  const [assessmentRetry, setAssessmentRetry] = useState(0);
  const [workspaceRoleView, setWorkspaceRoleView] = useState("");
  const [roleLoading, setRoleLoading] = useState(!existingProjectId);
  const [roleLoadError, setRoleLoadError] = useState(false);
  const [roleRetry, setRoleRetry] = useState(0);
  const planTier = user?.workspace_plan_tier || user?.plan_tier || "legacy";
  const projectLimit = { freelance: 1, business: 5, enterprise: null }[planTier] ?? null;
  const [data, setData] = useState({
    project: { name: "", description: "" },
    device_identity: { name: "", manufacturer: "", model: "", udi_di: "" },
    intended_purpose: { description: "", target_disease: "", body_part: "" },
    user_profile: { intended_users: "", training_required: "no", lay_user: "no" },
    clinical_context: {
      has_clinical_data: "no", literature_review: "no", equivalent_device: "no",
      equivalence_clinical: "no", equivalence_technical: "no", equivalence_biological: "no",
      pmcf_plan: "no", target_population: "", intended_clinical_benefit: "",
    },
    software_ai: { is_software: "no", uses_ai_ml: "no", software_purpose: "none", decision_impact: "low" },
    risk_profile: {
      invasive: "unknown", invasive_type: "unknown", duration: "unknown",
      active_device: "unknown", administers_energy: "unknown",
      energy_potentially_hazardous: "unknown", active_diagnostic_monitoring: "unknown",
      diagnostic_high_risk: "unknown", active_medicinal_administration: "unknown",
      active_use_hazardous: "unknown", xray_image_recorder: "unknown",
      inhalation_medicinal: "unknown", inhalation_essential_effect: "unknown",
      integrated_diagnostic_therapeutic: "unknown",
      contains_medicinal: "unknown", medicinal_substance_ancillary: "unknown",
      contains_nanomaterial: "unknown", nanomaterial_exposure: "unknown",
      animal_or_human_tissue: "unknown", tissue_origin: "unknown",
      animal_tissue_intact_skin_only: "unknown", sterilises_devices: "unknown",
      final_disinfection_invasive: "unknown", contraceptive: "unknown",
      channels_blood_or_meds: "unknown", blood_bag: "unknown",
      modifies_blood_composition: "unknown", modifies_blood_high_risk: "unknown",
      contact_injured_skin: "unknown", wound_category: "unknown",
      substance_via_orifice_or_skin: "unknown", substance_exposure: "unknown",
      reusable_surgical: "unknown", direct_contact_heart_cns_circulatory: "unknown",
      biological_effect_or_absorption: "unknown", rule8_specific_implant: "unknown",
    },
    existing_compliance: { iso_13485: "no", iso_14971: "no", iec_62304: "no", ce_marked_predecessor: "no" },
  });

  const STEPS = [
    t("wiz.step.project"),
    t("wiz.step.device"),
    t("wiz.step.purpose"),
    t("wiz.step.user"),
    t("wiz.step.clinical"),
    t("wiz.step.software"),
    t("wiz.step.risk"),
    t("wiz.step.compliance"),
    t("wiz.step.review"),
  ];
  const yesNo = [{ v: "yes", l: t("wiz.yes") }, { v: "no", l: t("wiz.no") }];
  const riskYesNo = [{ v: "unknown", l: t("demo.option.unknown") }, ...yesNo];

  useEffect(() => {
    let active = true;
    if (existingProjectId && existingDeviceId) {
      setCheckingProjectLimit(true);
      setExistingAssessmentLoadError(false);
      Promise.all([
        api.get(`/projects/${existingProjectId}`),
        api.get(`/devices/${existingDeviceId}`),
        api.get(`/projects/${existingProjectId}/versions`, { params: { device_id: existingDeviceId } }),
      ]).then(([projectResponse, deviceResponse, versionsResponse]) => {
        if (!active) return;
        const product = deviceResponse.data || {};
        const savedIntake = versionsResponse.data?.[0]?.intake || {};
        setData((current) => ({
          ...current,
            project: {
              name: projectResponse.data?.name || "",
              description: projectResponse.data?.description || "",
            },
          device_identity: {
            ...current.device_identity,
            ...savedIntake.device_identity,
            name: product.name || "",
            manufacturer: product.manufacturer_name || product.manufacturer || "",
            model: product.model || "",
            udi_di: product.udi_di || "",
          },
          intended_purpose: {
            ...current.intended_purpose,
            ...savedIntake.intended_purpose,
            description: product.intended_purpose || savedIntake.intended_purpose?.description || "",
          },
          user_profile: { ...current.user_profile, ...savedIntake.user_profile },
          clinical_context: { ...current.clinical_context, ...savedIntake.clinical_context },
          software_ai: { ...current.software_ai, ...savedIntake.software_ai },
          risk_profile: { ...current.risk_profile, ...savedIntake.risk_profile },
          existing_compliance: { ...current.existing_compliance, ...savedIntake.existing_compliance },
        }));
      }).catch(() => {
        if (active) setExistingAssessmentLoadError(true);
      }).finally(() => {
        if (active) setCheckingProjectLimit(false);
      });
      return () => { active = false; };
    }
    if (projectLimit === null) {
      setCheckingProjectLimit(false);
      return () => { active = false; };
    }
    setCheckingProjectLimit(true);
    api.get("/projects")
      .then(({ data }) => {
        if (!active) return;
        const projects = data || [];
        if (projects.length >= projectLimit) {
          setProjectLimitNotice({ current: projects.length, limit: projectLimit, plan: planTier });
        }
      })
      .catch(() => {
        // The API enforces the limit even if this pre-check cannot load.
      })
      .finally(() => {
        if (active) setCheckingProjectLimit(false);
      });
    return () => { active = false; };
  }, [planTier, projectLimit, existingProjectId, existingDeviceId, assessmentRetry, t]);

  useEffect(() => {
    if (existingProjectId) {
      setRoleLoading(false);
      return undefined;
    }
    let active = true;
    setRoleLoading(true);
    setRoleLoadError(false);
    api.get("/user/profile")
      .then(({ data }) => {
        if (active) setWorkspaceRoleView(data.operating_role_view || "");
      })
      .catch(() => {
        if (active) setRoleLoadError(true);
      })
      .finally(() => {
        if (active) setRoleLoading(false);
      });
    return () => { active = false; };
  }, [existingProjectId, roleRetry]);

  function update(section, key, value) {
    setData({ ...data, [section]: { ...data[section], [key]: value } });
  }

  async function submit() {
    setSubmitting(true);
    setErr("");
    try {
      let projectId = existingProjectId;
      let deviceId = existingDeviceId;
      if (!projectId) {
        const { data: project } = await api.post("/projects", {
          name: data.project.name,
          device_name: data.device_identity.name || "Unnamed device",
          description: data.project.description,
        });
        projectId = project.id;
        const { data: device } = await api.post(`/projects/${projectId}/devices`, {
          name: data.device_identity.name || "Unnamed device",
          manufacturer_name: data.device_identity.manufacturer || "",
          model: data.device_identity.model || "",
          udi_di: data.device_identity.udi_di || "",
          intended_purpose: data.intended_purpose.description || "",
        });
        deviceId = device?.id || "";
      }
      const intake = { ...data };
      delete intake.project;
      intake.lang = localStorage.getItem("rg_lang") || "en";
      if (!deviceId) throw new Error(t("wiz.create_failed"));
      await api.post(`/projects/${projectId}/analyze`, intake, {
        params: { device_id: deviceId },
      });
      nav(`/projects/${projectId}?section=classification&device_id=${encodeURIComponent(deviceId)}`);
    } catch (e) {
      const detail = e.response?.data?.detail;
      if (detail?.code === "project_limit_reached") {
        setProjectLimitNotice({
          current: detail.current,
          limit: detail.limit,
          plan: detail.plan,
          upgradePlan: detail.upgrade_plan,
        });
      } else {
        setErr(typeof detail === "string" ? detail : t("wiz.create_failed"));
      }
    } finally {
      setSubmitting(false);
    }
  }

  if (checkingProjectLimit || roleLoading) {
    return (
      <AppShell>
        <WorkspaceLoading message={existingProjectId ? t("workspace.loading_assessment") : t("plan.checking")} />
      </AppShell>
    );
  }

  if (roleLoadError && !existingProjectId) {
    return <AppShell><div role="alert" className="grid-card space-y-3 p-6 text-sm">
      <p>{t("company.role_view.load_error")}</p>
      <button type="button" onClick={() => setRoleRetry((current) => current + 1)} className="btn-primary px-4 py-2">{t("app.retry")}</button>
    </div></AppShell>;
  }

  if (!workspaceRoleView && !existingProjectId) {
    return <AppShell><section className="grid-card max-w-2xl space-y-3 p-6" data-testid="global-role-required">
      <h1 className="text-xl font-bold text-[var(--text)]">{t("company.role_view.required_title")}</h1>
      <p className="text-sm leading-relaxed text-[var(--text-2)]">{t("company.role_view.required_note")}</p>
      <Link to="/company" className="btn-primary inline-flex w-fit px-4 py-2 text-sm" data-testid="global-role-settings-link">
        {t("company.role_view.open_settings")}
      </Link>
    </section></AppShell>;
  }

  if (existingAssessmentLoadError) {
    return <AppShell><div role="alert" className="grid-card space-y-3 p-6 text-sm">
      <p>{t("workspace.load_error")}</p>
      <button type="button" onClick={() => setAssessmentRetry((current) => current + 1)} className="btn-primary px-4 py-2">{t("app.retry")}</button>
    </div></AppShell>;
  }

  if (projectLimitNotice) {
    return (
      <AppShell>
        <div className="grid-card p-6 text-sm text-[var(--text-2)]">
          {t("plan.limit.route_blocked")}
        </div>
        <ProjectLimitNotice
          planTier={projectLimitNotice.plan}
          current={projectLimitNotice.current}
          limit={projectLimitNotice.limit}
          onClose={() => nav("/dashboard")}
        />
      </AppShell>
    );
  }

  return (
    <AppShell>
      <div className="mb-6">
        <div className="text-xs font-mono text-slate-400 uppercase tracking-wider">Wizard</div>
        <h1 className="text-3xl font-extrabold tracking-tight">{t("wiz.title")}</h1>
        <p className="text-sm text-slate-400 mt-1">{t("wiz.subtitle_pre")} <span className="font-mono text-amber-400">"{t("wiz.not_provided")}"</span>.</p>
      </div>
      {err ? <div role="alert" className="mb-5 rounded-sm border border-red-500/40 bg-red-500/10 p-4 text-sm text-[var(--text)]">{String(err)}</div> : null}

      <div className="grid grid-cols-12 gap-6">
        <aside className="col-span-12 md:col-span-3 grid-card p-4 h-fit">
          <ol className="space-y-1">
            {STEPS.map((s, i) => (
              <li key={s} className={`text-sm py-1.5 px-2 flex items-center gap-2 rounded-sm transition-colors ${stepClass(i, step)}`}>
                <span className="font-mono text-[10px] w-5">{String(i + 1).padStart(2, "0")}</span>
                <span>{s}</span>
                {i < step && <Check size={12} className="ml-auto" />}
              </li>
            ))}
          </ol>
        </aside>

        <section className="col-span-12 md:col-span-9 grid-card p-6 min-h-[420px]" data-testid="wizard-section">
          <h2 className="text-xl font-bold mb-4">{STEPS[step]}</h2>

          {step === 0 && (
            <Grid cols={1}>
              <Input label={t("wiz.f.project_name")} value={data.project.name} onChange={(v) => update("project", "name", v)} testid="wiz-project-name" />
              <Input label={t("wiz.f.description")} value={data.project.description} onChange={(v) => update("project", "description", v)} testid="wiz-project-desc" />
            </Grid>
          )}
          {step === 1 && (
            <Grid>
              <Input label={t("wiz.f.device_name")} value={data.device_identity.name} onChange={(v) => update("device_identity", "name", v)} testid="wiz-dev-name" />
              <Input label={t("wiz.f.manufacturer")} value={data.device_identity.manufacturer} onChange={(v) => update("device_identity", "manufacturer", v)} testid="wiz-dev-mfr" />
              <Input label={t("wiz.f.model")} value={data.device_identity.model} onChange={(v) => update("device_identity", "model", v)} testid="wiz-dev-model" />
              <Input label={t("wiz.f.udi")} value={data.device_identity.udi_di} onChange={(v) => update("device_identity", "udi_di", v)} testid="wiz-dev-udi" />
            </Grid>
          )}
          {step === 2 && (
            <Grid cols={1}>
              <Input label={t("wiz.f.intended_purpose")} value={data.intended_purpose.description} onChange={(v) => update("intended_purpose", "description", v)} testid="wiz-ip-desc" textarea />
              <Input label={t("wiz.f.indication")} value={data.intended_purpose.target_disease} onChange={(v) => update("intended_purpose", "target_disease", v)} testid="wiz-ip-disease" />
              <Input label={t("wiz.f.body_part")} value={data.intended_purpose.body_part} onChange={(v) => update("intended_purpose", "body_part", v)} testid="wiz-ip-body" />
            </Grid>
          )}
          {step === 3 && (
            <Grid>
              <Input label={t("wiz.f.users")} value={data.user_profile.intended_users} onChange={(v) => update("user_profile", "intended_users", v)} testid="wiz-up-users" />
              <Select label={t("wiz.f.training")} value={data.user_profile.training_required} onChange={(v) => update("user_profile", "training_required", v)} options={yesNo} testid="wiz-up-train" />
              <Select label={t("wiz.f.lay")} value={data.user_profile.lay_user} onChange={(v) => update("user_profile", "lay_user", v)} options={yesNo} testid="wiz-up-lay" />
            </Grid>
          )}
          {step === 4 && (
            <Grid>
              <Select label={t("wiz.f.has_clinical")} value={data.clinical_context.has_clinical_data} onChange={(v) => update("clinical_context", "has_clinical_data", v)} options={yesNo} testid="wiz-cl-data" />
              <Select label={t("wiz.f.literature")} value={data.clinical_context.literature_review} onChange={(v) => update("clinical_context", "literature_review", v)} options={yesNo} testid="wiz-cl-lit" />
              <Select label={t("wiz.f.equiv")} value={data.clinical_context.equivalent_device} onChange={(v) => update("clinical_context", "equivalent_device", v)} options={yesNo} testid="wiz-cl-eq" />
              <Select label={t("wiz.f.pmcf")} value={data.clinical_context.pmcf_plan} onChange={(v) => update("clinical_context", "pmcf_plan", v)} options={yesNo} testid="wiz-cl-pmcf" />
              <Input label={t("wiz.f.population")} value={data.clinical_context.target_population} onChange={(v) => update("clinical_context", "target_population", v)} testid="wiz-cl-pop" />
              <Input label={t("wiz.f.benefit")} value={data.clinical_context.intended_clinical_benefit} onChange={(v) => update("clinical_context", "intended_clinical_benefit", v)} testid="wiz-cl-benefit" />
              {data.clinical_context.equivalent_device === "yes" && (
                <>
                  <Select label={t("wiz.f.equiv_c")} value={data.clinical_context.equivalence_clinical} onChange={(v) => update("clinical_context", "equivalence_clinical", v)} options={yesNo} testid="wiz-cl-eqc" />
                  <Select label={t("wiz.f.equiv_t")} value={data.clinical_context.equivalence_technical} onChange={(v) => update("clinical_context", "equivalence_technical", v)} options={yesNo} testid="wiz-cl-eqt" />
                  <Select label={t("wiz.f.equiv_b")} value={data.clinical_context.equivalence_biological} onChange={(v) => update("clinical_context", "equivalence_biological", v)} options={yesNo} testid="wiz-cl-eqb" />
                </>
              )}
            </Grid>
          )}
          {step === 5 && (
            <Grid>
              <Select label={t("wiz.f.is_sw")} value={data.software_ai.is_software} onChange={(v) => update("software_ai", "is_software", v)} options={yesNo} testid="wiz-sw-is" />
              <Select label={t("wiz.f.ai")} value={data.software_ai.uses_ai_ml} onChange={(v) => update("software_ai", "uses_ai_ml", v)} options={yesNo} testid="wiz-sw-ai" />
              <Select label={t("wiz.f.sw_purpose")} value={data.software_ai.software_purpose} onChange={(v) => update("software_ai", "software_purpose", v)}
                options={[{ v: "diagnosis", l: t("wiz.sp.diagnosis") }, { v: "therapy", l: t("wiz.sp.therapy") }, { v: "monitoring", l: t("wiz.sp.monitoring") }, { v: "admin", l: t("wiz.sp.admin") }, { v: "none", l: t("wiz.sp.none") }]} testid="wiz-sw-purpose" />
              <Select label={t("wiz.f.impact")} value={data.software_ai.decision_impact} onChange={(v) => update("software_ai", "decision_impact", v)}
                options={[{ v: "low", l: t("wiz.im.low") }, { v: "medium", l: t("wiz.im.medium") }, { v: "high", l: t("wiz.im.high") }]} testid="wiz-sw-impact" />
            </Grid>
          )}
          {step === 6 && (
            <Grid>
              <Select label={t("wiz.f.invasive")} value={data.risk_profile.invasive} onChange={(v) => update("risk_profile", "invasive", v)} options={riskYesNo} testid="wiz-rp-invasive" />
              <Select label={t("wiz.f.invasive_type")} value={data.risk_profile.invasive_type} onChange={(v) => update("risk_profile", "invasive_type", v)}
                options={[{ v: "unknown", l: t("demo.option.unknown") }, { v: "none", l: t("wiz.it.none") }, { v: "orifice", l: t("wiz.it.orifice") }, { v: "surgical", l: t("wiz.it.surgical") }]} testid="wiz-rp-itype" />
              <Select label={t("wiz.f.duration")} value={data.risk_profile.duration} onChange={(v) => update("risk_profile", "duration", v)}
                options={[{ v: "unknown", l: t("demo.option.unknown") }, { v: "transient", l: t("wiz.dur.transient") }, { v: "short_term", l: t("wiz.dur.short") }, { v: "long_term", l: t("wiz.dur.long") }]} testid="wiz-rp-dur" />
              <Select label={t("wiz.f.active")} value={data.risk_profile.active_device} onChange={(v) => update("risk_profile", "active_device", v)} options={riskYesNo} testid="wiz-rp-active" />
              <details className="col-span-full rounded-sm border border-[var(--border)] p-3">
                <summary className="cursor-pointer text-sm font-semibold text-[var(--accent-text)]">{t("demo.advanced_title")}</summary>
                <p className="my-3 text-xs leading-relaxed text-[var(--text-2)]">{t("demo.advanced_help")}</p>
                <div className="grid grid-cols-1 gap-3 md:grid-cols-3">
                  {[
                ["administers_energy", "wiz.f.energy", "wiz-rp-energy"],
                ["active_diagnostic_monitoring", "demo.f.active_diagnostic", "wiz-rp-active-diagnostic"],
                ["active_medicinal_administration", "demo.f.active_admin", "wiz-rp-active-admin"],
                ["xray_image_recorder", "demo.f.xray", "wiz-rp-xray"],
                ["inhalation_medicinal", "demo.f.inhalation", "wiz-rp-inhalation"],
                ["integrated_diagnostic_therapeutic", "demo.f.integrated_diagnostic", "wiz-rp-integrated"],
                ["energy_potentially_hazardous", "demo.f.energy_hazard", "wiz-rp-energy-risk"],
                ["diagnostic_high_risk", "demo.f.diagnostic_high_risk", "wiz-rp-diagnostic-risk"],
                ["active_use_hazardous", "demo.f.active_hazard", "wiz-rp-active-risk"],
                ["channels_blood_or_meds", "wiz.f.channels", "wiz-rp-channels"],
                ["blood_bag", "demo.f.blood_bag", "wiz-rp-blood-bag"],
                ["modifies_blood_composition", "wiz.f.modifies_blood", "wiz-rp-bloodmod"],
                ["modifies_blood_high_risk", "demo.f.blood_treatment", "wiz-rp-blood-risk"],
                ["contact_injured_skin", "wiz.f.injured_skin", "wiz-rp-skin"],
                ["contains_medicinal", "wiz.f.medicinal", "wiz-rp-med"],
                ["medicinal_substance_ancillary", "demo.f.medicinal_ancillary", "wiz-rp-med-ancillary"],
                ["contains_nanomaterial", "wiz.f.nano", "wiz-rp-nano"],
                ["animal_or_human_tissue", "wiz.f.tissue", "wiz-rp-tissue"],
                ["animal_tissue_intact_skin_only", "demo.f.intact_skin_only", "wiz-rp-tissue-skin"],
                ["sterilises_devices", "wiz.f.sterilises", "wiz-rp-ster"],
                ["final_disinfection_invasive", "demo.f.final_disinfection", "wiz-rp-final-disinfection"],
                ["contraceptive", "wiz.f.contraceptive", "wiz-rp-contra"],
                ["substance_via_orifice_or_skin", "demo.f.substance", "wiz-rp-substance"],
                ["reusable_surgical", "demo.f.reusable_surgical", "wiz-rp-reusable"],
                ["direct_contact_heart_cns_circulatory", "demo.f.critical_contact", "wiz-rp-critical-contact"],
                ["biological_effect_or_absorption", "demo.f.bio_effect", "wiz-rp-bio"],
                ["rule8_specific_implant", "demo.f.specific_implant", "wiz-rp-rule8"],
                  ].map(([key, labelKey, testid]) => (
                    <Select key={`${key}-${testid}`} label={t(labelKey)} value={data.risk_profile[key]} onChange={(v) => update("risk_profile", key, v)} options={riskYesNo} testid={testid} />
                  ))}
                  <Select label={t("demo.f.wound_category")} value={data.risk_profile.wound_category} onChange={(v) => update("risk_profile", "wound_category", v)}
                    options={[{ v: "unknown", l: t("demo.option.unknown") }, { v: "mechanical_barrier", l: t("demo.wound.barrier") }, { v: "superficial", l: t("demo.wound.superficial") }, { v: "deep_secondary_intention", l: t("demo.wound.deep") }]} testid="wiz-rp-wound" />
                  <Select label={t("demo.f.tissue_origin")} value={data.risk_profile.tissue_origin} onChange={(v) => update("risk_profile", "tissue_origin", v)}
                    options={[{ v: "unknown", l: t("demo.option.unknown") }, { v: "human", l: t("demo.tissue.human") }, { v: "animal", l: t("demo.tissue.animal") }]} testid="wiz-rp-tissue-origin" />
                  <Select label={t("demo.f.nano_exposure")} value={data.risk_profile.nanomaterial_exposure} onChange={(v) => update("risk_profile", "nanomaterial_exposure", v)}
                    options={[{ v: "unknown", l: t("demo.option.unknown") }, { v: "none", l: t("demo.nano.none") }, { v: "high", l: t("demo.nano.high") }, { v: "medium", l: t("demo.nano.medium") }, { v: "low", l: t("demo.nano.low") }]} testid="wiz-rp-nano-exposure" />
                  <Select label={t("demo.f.substance_exposure")} value={data.risk_profile.substance_exposure} onChange={(v) => update("risk_profile", "substance_exposure", v)}
                    options={[{ v: "unknown", l: t("demo.option.unknown") }, { v: "systemic", l: t("demo.substance.systemic") }, { v: "stomach_lower_gi", l: t("demo.substance.gi") }, { v: "skin_nasal_oral", l: t("demo.substance.local_sites") }, { v: "other_local", l: t("demo.substance.other") }]} testid="wiz-rp-substance-exposure" />
                  <Select label={t("demo.f.inhalation_essential")} value={data.risk_profile.inhalation_essential_effect} onChange={(v) => update("risk_profile", "inhalation_essential_effect", v)} options={riskYesNo} testid="wiz-rp-inhalation-essential" />
                </div>
              </details>
            </Grid>
          )}
          {step === 7 && (
            <Grid>
              <Select label={t("wiz.f.13485")} value={data.existing_compliance.iso_13485} onChange={(v) => update("existing_compliance", "iso_13485", v)} options={yesNo} testid="wiz-ec-13485" />
              <Select label={t("wiz.f.14971")} value={data.existing_compliance.iso_14971} onChange={(v) => update("existing_compliance", "iso_14971", v)} options={yesNo} testid="wiz-ec-14971" />
              <Select label={t("wiz.f.62304")} value={data.existing_compliance.iec_62304} onChange={(v) => update("existing_compliance", "iec_62304", v)} options={yesNo} testid="wiz-ec-62304" />
              <Select label={t("wiz.f.ce")} value={data.existing_compliance.ce_marked_predecessor} onChange={(v) => update("existing_compliance", "ce_marked_predecessor", v)} options={yesNo} testid="wiz-ec-ce" />
            </Grid>
          )}
          {step === 8 && (
            <div>
              <p className="text-sm text-zinc-600">{t("wiz.review_hint")} <span className="font-mono text-amber-600">"{t("wiz.not_provided")}"</span>.</p>
              <div className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-3">
                <ReviewCard title={t("wiz.step.device")} rows={[
                  [t("wiz.f.device_name"), data.device_identity.name],
                  [t("wiz.f.manufacturer"), data.device_identity.manufacturer],
                  [t("wiz.f.model"), data.device_identity.model],
                  [t("wiz.f.udi"), data.device_identity.udi_di],
                ]} />
                <ReviewCard title={t("wiz.step.purpose")} rows={[
                  [t("wiz.f.intended_purpose"), data.intended_purpose.description],
                  [t("wiz.f.indication"), data.intended_purpose.target_disease],
                  [t("wiz.f.body_part"), data.intended_purpose.body_part],
                ]} />
                <ReviewCard title={t("wiz.step.user")} rows={[
                  [t("wiz.f.users"), data.user_profile.intended_users],
                  [t("wiz.f.training"), data.user_profile.training_required],
                  [t("wiz.f.lay"), data.user_profile.lay_user],
                ]} />
                <ReviewCard title={t("wiz.step.software")} rows={[
                  [t("wiz.f.is_sw"), data.software_ai.is_software],
                  [t("wiz.f.ai"), data.software_ai.uses_ai_ml],
                  [t("wiz.f.sw_purpose"), data.software_ai.software_purpose],
                  [t("wiz.f.impact"), data.software_ai.decision_impact],
                ]} />
                <ReviewCard title={t("wiz.step.risk")} rows={[
                  [t("wiz.f.invasive"), data.risk_profile.invasive],
                  [t("wiz.f.invasive_type"), data.risk_profile.invasive_type],
                  [t("wiz.f.duration"), data.risk_profile.duration],
                  [t("wiz.f.active"), data.risk_profile.active_device],
                ]} />
                <ReviewCard title={t("wiz.step.compliance")} rows={[
                  [t("wiz.f.13485"), data.existing_compliance.iso_13485],
                  [t("wiz.f.14971"), data.existing_compliance.iso_14971],
                  [t("wiz.f.62304"), data.existing_compliance.iec_62304],
                  [t("wiz.f.ce"), data.existing_compliance.ce_marked_predecessor],
                ]} />
              </div>
            </div>
          )}

          <div className="mt-8 flex items-center justify-between border-t border-zinc-100 pt-4">
            <button data-testid="wiz-prev" disabled={step === 0} onClick={() => setStep(step - 1)}
              className="inline-flex items-center gap-1 px-3 py-2 border border-zinc-200 rounded-sm text-sm hover:bg-blue-50 hover:border-blue-300 transition-colors disabled:opacity-40">
              <CaretLeft size={14} /> {t("wiz.back")}
            </button>
            {step < STEPS.length - 1 ? (
              <button data-testid="wiz-next" onClick={() => setStep(step + 1)} className="btn-primary inline-flex items-center gap-1 px-4 py-2 text-sm">
                {t("wiz.continue")} <CaretRight size={14} />
              </button>
            ) : (
              <button data-testid="wiz-submit" disabled={submitting} onClick={submit} className="btn-primary inline-flex items-center gap-1 px-4 py-2 text-sm">
                {submitting ? t("wiz.submitting") : (existingProjectId ? t("wiz.update_assessment") : t("wiz.submit"))}
              </button>
            )}
          </div>
        </section>
      </div>
    </AppShell>
  );
}

function Grid({ children, cols = 2 }) {
  return <div className={`grid grid-cols-1 md:grid-cols-${cols} gap-4`}>{children}</div>;
}

function ReviewCard({ title, rows }) {
  return (
    <div className="border border-zinc-200 rounded-sm p-4 bg-white">
      <div className="text-xs font-mono uppercase tracking-wider text-zinc-500 mb-2">{title}</div>
      <dl className="space-y-1 text-sm">
        {rows.map(([k, v], i) => (
          <div key={k} className="flex gap-3 border-b border-zinc-50 py-1 last:border-0">
            <dt className="text-zinc-500 flex-shrink-0 w-2/5">{k}</dt>
            <dd className={`flex-1 ${v ? "text-zinc-900" : "text-amber-600 italic"}`}>{v || "—"}</dd>
          </div>
        ))}
      </dl>
    </div>
  );
}

function Input({ label, value, onChange, testid, textarea }) {
  return (
    <div>
      <label className="text-xs font-semibold tracking-wide uppercase text-zinc-600">{label}</label>
      {textarea ? (
        <textarea data-testid={testid} value={value} onChange={(e) => onChange(e.target.value)} rows={3}
          className="mt-1 w-full border border-zinc-200 px-3 py-2 rounded-sm focus:border-blue-600 focus:ring-2 focus:ring-blue-100 focus:outline-none transition-colors text-sm" />
      ) : (
        <input data-testid={testid} value={value} onChange={(e) => onChange(e.target.value)}
          className="mt-1 w-full border border-zinc-200 px-3 py-2 rounded-sm focus:border-blue-600 focus:ring-2 focus:ring-blue-100 focus:outline-none transition-colors text-sm" />
      )}
    </div>
  );
}

function Select({ label, value, onChange, options, testid }) {
  return (
    <div>
      <label className="text-xs font-semibold tracking-wide uppercase text-zinc-600">{label}</label>
      <select data-testid={testid} value={value} onChange={(e) => onChange(e.target.value)}
        className="mt-1 w-full border border-zinc-200 px-3 py-2 rounded-sm focus:border-blue-600 focus:ring-2 focus:ring-blue-100 focus:outline-none transition-colors text-sm bg-white">
        {options.map((o) => <option key={o.v} value={o.v}>{o.l}</option>)}
      </select>
    </div>
  );
}
